// Enhanced translation manager that uses AI for dynamic, meaningful translations
import { AITranslationService } from "./aiTranslationService";
import { setGoogleTranslateLanguage } from "./translationUtils";

export class EnhancedTranslationManager {
  private static instance: EnhancedTranslationManager;
  private aiService: AITranslationService;
  private originalTexts: Map<Element, string> = new Map();
  private isCustomTranslationApplied = false;
  private translationQueue: string[] = [];
  private isProcessing = false;
  private lastTranslationTime = 0;
  private translationCooldown = 10000; // 10 seconds cooldown

  static getInstance(): EnhancedTranslationManager {
    if (!EnhancedTranslationManager.instance) {
      EnhancedTranslationManager.instance = new EnhancedTranslationManager();
    }
    return EnhancedTranslationManager.instance;
  }

  constructor() {
    this.aiService = AITranslationService.getInstance();
  }

  // Store original text before translation
  private storeOriginalTexts() {
    const walker = document.createTreeWalker(
      document.body,
      NodeFilter.SHOW_TEXT,
      {
        acceptNode: (node) => {
          const text = node.textContent?.trim() || "";
          return text.length > 0 &&
            text.length < 200 &&
            !text.match(/^\d+$/) &&
            !text.match(/^[^\w\s]+$/) &&
            !node.parentElement?.classList.contains("notranslate")
            ? NodeFilter.FILTER_ACCEPT
            : NodeFilter.FILTER_REJECT;
        },
      }
    );

    let node;
    while ((node = walker.nextNode())) {
      const textNode = node as Text;
      if (!this.originalTexts.has(textNode as any)) {
        this.originalTexts.set(textNode as any, textNode.textContent || "");
      }
    }
  }

  // Process translation queue
  private async processTranslationQueue(language: "si" | "ta") {
    if (this.isProcessing || this.translationQueue.length === 0) {
      return;
    }

    // Check cooldown period
    const now = Date.now();
    if (now - this.lastTranslationTime < this.translationCooldown) {
      console.log("⏳ Translation cooldown active, skipping...");
      return;
    }

    this.isProcessing = true;
    this.lastTranslationTime = now;
    console.log(
      `🔄 Processing ${this.translationQueue.length} texts for translation...`
    );

    try {
      // Get unique texts to translate
      const uniqueTexts = [...new Set(this.translationQueue)];

      // Translate in smaller batches to avoid API limits
      const batchSize = 1; // Reduce to 1 text at a time
      for (let i = 0; i < uniqueTexts.length; i += batchSize) {
        const batch = uniqueTexts.slice(i, i + batchSize);

        try {
          const translations = await this.aiService.translateBatch(
            batch,
            language
          );

          // Apply translations to DOM
          this.applyTranslationsToDOM(batch, translations);
        } catch (error) {
          console.warn("⚠️ Translation batch failed, skipping:", error);
        }

        // Longer delay between each request to avoid rate limiting
        if (i + batchSize < uniqueTexts.length) {
          await new Promise((resolve) => setTimeout(resolve, 5000)); // 5 second delay
        }
      }

      console.log("✅ AI translation queue processed");
    } catch (error) {
      console.error("❌ Error processing translation queue:", error);
    } finally {
      this.isProcessing = false;
      this.translationQueue = [];
    }
  }

  // Apply translations to DOM elements
  private applyTranslationsToDOM(
    originalTexts: string[],
    translations: string[]
  ) {
    const walker = document.createTreeWalker(
      document.body,
      NodeFilter.SHOW_TEXT,
      {
        acceptNode: (node) => {
          const text = node.textContent?.trim() || "";

          // Check if this text node is inside a notranslate element
          let parent = node.parentElement;
          while (parent) {
            if (parent.classList.contains("notranslate")) {
              return NodeFilter.FILTER_REJECT;
            }
            parent = parent.parentElement;
          }

          // Check if text contains protected brand terms
          if (
            text.includes("MealBridge") ||
            text.includes("SRI LANKA") ||
            text.includes("Sri Lanka")
          ) {
            return NodeFilter.FILTER_REJECT;
          }

          return text.length > 0 &&
            text.length < 200 &&
            !text.match(/^\d+$/) &&
            !text.match(/^[^\w\s]+$/)
            ? NodeFilter.FILTER_ACCEPT
            : NodeFilter.FILTER_REJECT;
        },
      }
    );

    let node;
    while ((node = walker.nextNode())) {
      const textNode = node as Text;
      const originalText = textNode.textContent || "";

      // Find matching translation
      const index = originalTexts.findIndex((text) => text === originalText);
      if (index !== -1 && translations[index]) {
        // Apply translation without adding any highlighting styles
        textNode.textContent = translations[index];

        // Ensure parent element doesn't have highlighting styles
        if (textNode.parentElement) {
          textNode.parentElement.style.userSelect = "none";
          textNode.parentElement.style.webkitUserSelect = "none";
          textNode.parentElement.classList.remove(
            "highlight",
            "translate",
            "selected",
            "translated"
          );
        }

        console.log(
          `🔄 Translated: "${originalText}" → "${translations[index]}"`
        );
      }
    }
  }

  // Apply AI-powered custom translation
  async applyAITranslation(language: "si" | "ta" | "en") {
    console.log(`🎯 Applying AI translation for: ${language}`);

    if (language === "en") {
      await this.restoreToEnglish();
      return;
    }

    // Store original texts if not already stored
    if (this.originalTexts.size === 0) {
      this.storeOriginalTexts();
    }

    // Wait a bit for Google Translate to process first
    await new Promise((resolve) => setTimeout(resolve, 1000));

    // Collect all texts that need translation
    this.translationQueue = [];
    const walker = document.createTreeWalker(
      document.body,
      NodeFilter.SHOW_TEXT,
      {
        acceptNode: (node) => {
          const text = node.textContent?.trim() || "";

          // Check if this text node is inside a notranslate element
          let parent = node.parentElement;
          while (parent) {
            if (parent.classList.contains("notranslate")) {
              return NodeFilter.FILTER_REJECT;
            }
            parent = parent.parentElement;
          }

          // Check if text contains protected brand terms
          if (
            text.includes("MealBridge") ||
            text.includes("SRI LANKA") ||
            text.includes("Sri Lanka")
          ) {
            return NodeFilter.FILTER_REJECT;
          }

          // Skip single letters (like "M" in logos)
          if (text.length === 1 && /[A-Za-z]/.test(text)) {
            return NodeFilter.FILTER_REJECT;
          }

          if (
            text.length > 0 &&
            text.length < 200 &&
            !text.match(/^\d+$/) &&
            !text.match(/^[^\w\s]+$/)
          ) {
            this.translationQueue.push(text);
            return NodeFilter.FILTER_ACCEPT;
          }
          return NodeFilter.FILTER_REJECT;
        },
      }
    );

    // Walk through all text nodes
    while (walker.nextNode()) {
      // Text is already collected in acceptNode
    }

    // Process the translation queue
    await this.processTranslationQueue(language);

    this.isCustomTranslationApplied = true;
    console.log(`✅ AI translation applied for: ${language}`);
  }

  // Restore to English
  async restoreToEnglish() {
    console.log("🔄 Restoring to English...");

    // Clear any custom translations first
    this.isCustomTranslationApplied = false;

    // Clear Google Translate cookies
    const cookies = [
      "googtrans",
      "googtrans=/en/en",
      "googtrans=/en/si",
      "googtrans=/en/ta",
      "googtrans=/si/en",
      "googtrans=/ta/en",
      "googtrans=/si/si",
      "googtrans=/ta/ta",
    ];

    cookies.forEach((cookie) => {
      document.cookie = `${cookie}=; Path=/; Expires=Thu, 01 Jan 1970 00:00:01 GMT;`;
      document.cookie = `${cookie}=; Path=/; Domain=${window.location.hostname}; Expires=Thu, 01 Jan 1970 00:00:01 GMT;`;
      document.cookie = `${cookie}=; Path=/; Domain=.${window.location.hostname}; Expires=Thu, 01 Jan 1970 00:00:01 GMT;`;
    });

    // Set English as the default language
    document.cookie =
      "googtrans=/en/en; Path=/; Domain=" + window.location.hostname;
    document.cookie =
      "googtrans=/en/en; Path=/; Domain=." + window.location.hostname;
    document.cookie = "googtrans=/en/en; Path=/";

    // Clear translation state
    const body = document.body;
    if (body) {
      body.classList.remove("translated-ltr", "translated-rtl");
      body.style.direction = "ltr";
    }

    // Hide all Google Translate elements
    const googleElements = document.querySelectorAll('[class*="goog-te"]');
    googleElements.forEach((el) => {
      if (el instanceof HTMLElement) {
        el.style.display = "none";
      }
    });

    // Try to reset Google Translate combo if available
    const combo = document.querySelector(".goog-te-combo") as HTMLSelectElement;
    if (combo) {
      console.log("🔍 Found combo, resetting to English");

      // Try different values that might represent English
      const englishValues = ["0", "", "en", "auto"];

      for (const value of englishValues) {
        combo.value = value;
        combo.dispatchEvent(new Event("change", { bubbles: true }));
        combo.dispatchEvent(new Event("input", { bubbles: true }));
      }

      console.log("✅ English reset attempted");
    }

    // Restore original texts if we have them stored
    if (this.originalTexts.size > 0) {
      console.log("🔄 Restoring original texts...");
      this.originalTexts.forEach((originalText, textNode) => {
        if (textNode && textNode.textContent !== originalText) {
          textNode.textContent = originalText;
        }
      });
      this.originalTexts.clear();
    }

    console.log("✅ Restored to English");
  }

  // Enhanced translation with AI
  async translateTo(language: "si" | "ta" | "en") {
    if (language === "en") {
      await this.restoreToEnglish();
      return;
    }

    try {
      // First apply Google Translate
      const success = await setGoogleTranslateLanguage(language);

      if (success) {
        // Then apply AI translations for better quality
        setTimeout(() => {
          this.applyAITranslation(language);
        }, 2000); // Give more time for Google Translate to process
      } else {
        console.error("❌ Failed to set Google Translate language");
      }
    } catch (error) {
      console.error("❌ Error translating to", language, ":", error);
    }
  }

  // Check if custom translation is applied
  isCustomTranslationActive(): boolean {
    return this.isCustomTranslationApplied;
  }

  // Get current language from Google Translate
  getCurrentLanguage(): string {
    const combo = document.querySelector(".goog-te-combo") as HTMLSelectElement;
    return combo?.value || "en";
  }

  // Check if AI translation is available
  isAITranslationAvailable(): boolean {
    return this.aiService.isAITranslationAvailable();
  }

  // Get cache statistics
  getCacheStats() {
    return this.aiService.getCacheStats();
  }

  // Clear translation cache
  clearCache() {
    this.aiService.clearCache();
  }
}
